import type { ReactNode } from "react";
import { ArrowLeft, ArrowLeftRight, CircleCheck } from "lucide-react";
import { useTranslation } from "../../i18n";
import { DispatchPointsPill } from "./dispatch-session-chrome";

/** One "already set" fact — the desk or tote the packer is working from.
 *  With an onChange it is a button that takes them back to rescan it;
 *  without one (e.g. an order is open, so the desk can't change) it is a
 *  plain label. */
const SessionChip = ({ label, value, onChange, changeLabel }: {
    label: string;
    value: string;
    onChange?: () => void;
    changeLabel: string;
}) => {
    const body = (
        <>
            <CircleCheck size={18} className="dispatch-session-chip-check" aria-hidden="true" />
            <span className="dispatch-session-chip-label">{label}</span>
            <span className="dispatch-session-chip-value">{value}</span>
        </>
    );
    if (!onChange) return <span className="dispatch-session-chip">{body}</span>;
    return (
        <button
            type="button"
            className="dispatch-session-chip dispatch-session-chip--button"
            onClick={onChange}
            aria-label={`${changeLabel}: ${value}`}
            title={changeLabel}
        >
            {body}
            <ArrowLeftRight size={14} className="dispatch-session-chip-swap" aria-hidden="true" />
        </button>
    );
};

type Props = {
    deskId?: string;
    onChangeDesk?: () => void;
    toteLabel?: string;
    onChangeTote?: () => void;
    /** Shown on the scan screens once a desk is set; the desk scan has no
     *  session to score yet. */
    showPoints?: boolean;
    /** Extra right-hand controls (the Reopen button). */
    extra?: ReactNode;
    onBack: () => void;
};

/** Top bar of the dispatch scan screens (desk, tote, item). Says what is
 *  already set — desk and tote as labelled chips — instead of a bare
 *  "DISPATCH" title. The item screen used to show neither, and the tote
 *  screen's change-desk control was an unlabelled "D-04". */
const DispatchSessionBar = ({ deskId, onChangeDesk, toteLabel, onChangeTote, showPoints, extra, onBack }: Props) => {
    const { t } = useTranslation();
    const hasChips = !!deskId || !!toteLabel;
    return (
        <div className="dispatch-session-bar">
            <h1 className="dispatch-session-bar-title">{t("dispatch")}</h1>
            {showPoints && <DispatchPointsPill />}
            {hasChips && <span className="dispatch-session-bar-divider" aria-hidden="true" />}
            {deskId && (
                <SessionChip
                    label={t("dispatch_chip_desk")}
                    value={deskId}
                    onChange={onChangeDesk}
                    changeLabel={t("dispatch_change_desk")}
                />
            )}
            {toteLabel && (
                <SessionChip
                    label={t("dispatch_chip_tote")}
                    value={toteLabel}
                    onChange={onChangeTote}
                    changeLabel={t("dispatch_change_tote")}
                />
            )}
            <div className="dispatch-session-bar-spacer" />
            {extra}
            <button type="button" className="dispatch-bar-icon-btn" onClick={onBack} aria-label={t("back")}>
                <ArrowLeft size={20} />
            </button>
        </div>
    );
};

export default DispatchSessionBar;
